import { TrackerPosition } from "@/interfaces";
import { formatDateTime } from "@/lib/utils";
import L from "leaflet";
import icon from "leaflet/dist/images/marker-icon.png";
import React, { useEffect, useRef, useState } from "react";
import { Marker, Polyline } from "react-leaflet";
import AnimationControls from "./AnimationControl";
import { RouteAnimationLabelIcon } from "./Icons";

const customIcon = new L.Icon({
  iconUrl: icon,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

interface PositionData {
  lat: number;
  lng: number;
  timestamp: number;
  hour: string;
}

const AnimationManager: React.FC<{
  routeData: TrackerPosition[];
  setShowIcons: (state: boolean) => void;
  setShowLabels: (state: boolean) => void;
  setIsAnimating: (state: boolean) => void;
  isAnimating: boolean;
}> = ({
  routeData,
  setShowIcons,
  setShowLabels,
  setIsAnimating,
  isAnimating,
}) => {
  const [showHour, setShowHour] = useState(false);
  const markerRef = useRef<L.Marker | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [currentPosition, setCurrentPosition] = useState<L.LatLng>(
    new L.LatLng(routeData[0]?.latitude || 0, routeData[0]?.longitude || 0),
  );
  const [currentPositionHour, setCurrentPositionHour] = useState<string>(
    formatDateTime(routeData[0].date, true),
  );
  const [routePath, setRoutePath] = useState<L.LatLng[]>([
    new L.LatLng(routeData[0]?.latitude || 0, routeData[0]?.longitude || 0),
  ]);

  // Preparar dados com timestamps
  const prepareDataset = (coords: TrackerPosition[]) => {
    const dataset = [] as PositionData[];
    const timestamp = new Date().getTime();
    let findFirstIgnition = false;

    coords.forEach((coord, index) => {
      if (coord.ignition && !findFirstIgnition) {
        findFirstIgnition = true;
      }

      if (!findFirstIgnition) return;

      dataset.push({
        lng: coord.longitude,
        lat: coord.latitude,
        timestamp: timestamp + index * 500, // Intervalo de 500ms entre pontos
        hour: formatDateTime(coord.date, true),
      });
    });

    return dataset;
  };

  // Reset state when routeData changes
  useEffect(() => {
    const startPosition = new L.LatLng(
      routeData[0]?.latitude || 0,
      routeData[0]?.longitude || 0,
    );

    // Reset all state
    setIsAnimating(false);
    setShowHour(false);
    setCurrentPosition(startPosition);
    setCurrentPositionHour("");
    setRoutePath([startPosition]);

    // Clear any ongoing animations
    if (intervalRef.current) clearInterval(intervalRef.current);

    // Reset marker position
    if (markerRef.current) {
      markerRef.current.setLatLng([startPosition.lat, startPosition.lng]);
    }
  }, [routeData, setIsAnimating, setShowIcons, setShowLabels]);

  // Função para interpolar suavemente entre dois pontos
  const interpolatePosition = (
    start: L.LatLng,
    end: L.LatLng,
    progress: number, // 0 a 1, onde 1 é o ponto final
  ) => {
    const lat = start.lat + (end.lat - start.lat) * progress;
    const lng = start.lng + (end.lng - start.lng) * progress;
    return new L.LatLng(lat, lng);
  };

  // Função para animar o movimento entre dois pontos
  const animateToPosition = (
    start: L.LatLng,
    end: L.LatLng,
    duration: number,
    positionData: PositionData,
  ) => {
    let startTime: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1); // Normaliza o progresso entre 0 e 1

      const newPosition = interpolatePosition(start, end, progress);
      setCurrentPosition(newPosition);
      setCurrentPositionHour(positionData.hour);
      setRoutePath((prev) => [...prev, newPosition]);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  };

  // Função para iniciar a animação
  const handlePlay = () => {
    // Hide markers
    setShowIcons(false);
    setShowLabels(false);

    setShowHour(true);

    if (!isAnimating) {
      setIsAnimating(true);
      const dataset = prepareDataset(routeData);
      setRoutePath([new L.LatLng(dataset[0].lat, dataset[0].lng)]);
      startAnimation(dataset);
    }
  };

  // Função para parar a animação
  const handleStop = () => {
    setShowHour(false);

    // Always clear any ongoing intervals and animations
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Reset animation state
    setIsAnimating(false);

    // Reset marker and position to starting point
    const startPosition = new L.LatLng(
      routeData[0].latitude,
      routeData[0].longitude,
    );

    setTimeout(() => {
      setShowLabels(true);

      // Update both the state and the marker position
      setCurrentPosition(startPosition);
      if (markerRef.current) {
        markerRef.current.setLatLng(startPosition);
      }
    }, 500); // Delay to match animation duration

    // Reset route and index
    setRoutePath([]);
  };

  const startAnimation = (dataset: PositionData[]) => {
    let currentIndex = 0;
    let tick = dataset[0].timestamp;

    intervalRef.current = setInterval(() => {
      const endTime = dataset[dataset.length - 1].timestamp;

      if (tick > endTime) {
        handleStop();
        return;
      }

      // Encontrar o próximo ponto
      while (
        currentIndex < dataset.length - 1 &&
        dataset[currentIndex + 1].timestamp <= tick
      ) {
        currentIndex++;
      }

      if (currentIndex < dataset.length - 1) {
        const startPos = new L.LatLng(
          dataset[currentIndex].lat,
          dataset[currentIndex].lng,
        );
        const endPos = new L.LatLng(
          dataset[currentIndex + 1].lat,
          dataset[currentIndex + 1].lng,
        );

        // Animação suave para o próximo ponto
        animateToPosition(startPos, endPos, 500, dataset[currentIndex]); // Duração de 500ms por segmento

        currentIndex++;
      }

      tick += 500;
    }, 500); // Atualiza a cada 500ms (tempo entre pontos)
  };

  return (
    <>
      {/* Vehicle Marker */}
      <React.Fragment>
        {isAnimating && (
          <Marker
            position={currentPosition}
            key={`vehicle-animate`}
            icon={customIcon}
            ref={markerRef}
          />
        )}

        {showHour && (
          <Marker
            position={currentPosition}
            icon={RouteAnimationLabelIcon(currentPositionHour)}
          />
        )}
      </React.Fragment>

      {/* Dynamic Route Polyline */}
      <Polyline
        positions={routePath}
        pathOptions={{
          color: "blue", // Customize color as needed
          weight: 3,
        }}
      />

      {/* Animation Controls */}
      <AnimationControls
        onPlay={handlePlay}
        onPause={handleStop}
        onStop={handleStop}
        isPlaying={isAnimating}
        isPaused={!isAnimating}
      />
    </>
  );
};

export default AnimationManager;
